import React from 'react'

import { CardItem } from '@/components/CardItem'
import { Transaction } from '@/domain'

interface ActivityImageProps {
  uri?: string
  backgroundColor?: string | [string, string, ...string[]]
  name?: string
  isSentToNonUser?: boolean
  iconName?: string
  iconSize?: number
  transaction: Transaction
}

export const ActivityImage: React.FC<ActivityImageProps> = ({
  uri,
  backgroundColor,
  name,
  isSentToNonUser,
  iconName,
  iconSize,
  transaction,
}) => {
  // Determine transaction type conditions like in RN version
  const isKemToKem =
    transaction.type === 'kem_to_kem' || transaction.type === 'on_ramp_send'
  const isSavingsTransferIn = transaction.type === 'saving_transfer_in'
  const isConvert =
    transaction.type === 'swap_convert' || transaction.type === 'swap_sell'
  const isCardWriteOff = transaction.type === 'card_write_off'

  // Parse background color for gradients
  const parsedBgColor = Array.isArray(backgroundColor)
    ? backgroundColor
    : typeof backgroundColor === 'string' && backgroundColor.includes(',')
      ? (backgroundColor.split(',') as [string, string, ...string[]])
      : (backgroundColor as string)

  // Get icon name logic
  const getIconName = (): string | undefined => {
    if (iconName) return iconName
    if (isSentToNonUser) return 'users'
    return undefined
  }

  const finalIconName = getIconName()

  // For conversions, show target currency icon like in RN
  if (isConvert && transaction.subject_swap?.credit_amount) {
    const targetCurrency = transaction.subject_swap.credit_amount.currency
    return (
      <CardItem.CurrencyIcon
        uri={uri || ''} // Use passed URI (should be target currency icon)
        bgColor={(backgroundColor as string) || ''} // Use passed background color
        size={48}
        fallbackText={targetCurrency.charAt(0) || '?'}
      />
    )
  }

  // For card_write_off, show card icon with purple background (like TransactionItem)
  if (isCardWriteOff) {
    return (
      <CardItem.AvatarGradient
        colors={['#8B5CF6', '#8B5CF6']}
        iconName="card"
        iconSize={iconSize || 28}
        size={48}
        start={[1, 0]}
        end={[0, 1]}
      />
    )
  }

  // If not kem-to-kem and no icon, show currency icon
  if (!isKemToKem && !finalIconName) {
    return (
      <CardItem.CurrencyIcon
        uri={uri}
        bgColor={(backgroundColor as string) || ''}
        size={48}
        fallbackText={transaction.amount?.currency?.charAt(0) || '?'}
      />
    )
  }

  // If no URI and has gradient colors with icon (includes card transactions and special transaction types)
  if (
    !uri &&
    Array.isArray(parsedBgColor) &&
    (finalIconName || isSentToNonUser || isSavingsTransferIn)
  ) {
    return (
      <CardItem.AvatarGradient
        colors={parsedBgColor}
        name={name}
        iconName={finalIconName}
        iconSize={iconSize}
        size={48}
        start={[1, 0]}
        end={[0, 1]}
      />
    )
  }

  // Default avatar
  return <CardItem.Avatar uri={uri} name={name || ''} size={48} />
}
